import React, { MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';

import { Text as ReffableText } from '@betterment/samba-react';
import { BoxProps, ReffableBox, theme } from '@betterment/style-closet-react';
import styled from '@emotion/styled';
import ContentLoader from 'react-content-loader';
import { FontSizeProps, LineHeightProps, ResponsiveValue } from 'styled-system';
import { useDebouncedCallback } from 'use-debounce';

type TextAlignment = 'center' | 'left' | 'right';
export interface TextShimmerLoaderProps extends FontSizeProps, LineHeightProps, BoxProps {
  textAlign?: ResponsiveValue<TextAlignment>;
  width?: BoxProps['width'];
  numLines?: number;
}

interface TextShimmerDimensions {
  containerWidth: number;
  lineHeight: number;
  fontSize: number;
  textAlign: TextAlignment;
}

const HiddenText = styled(ReffableText)`
  position: absolute;
  top: -99999px;
  left: -99999px;
  visibility: hidden;
`;

const useResizeCallback = (callback: () => void) => {
  const debounced = useDebouncedCallback(callback, 500).callback;

  useEffect(() => {
    callback();
    window.addEventListener('resize', debounced);

    return () => {
      window.removeEventListener('resize', debounced);
    };
  }, [callback, debounced]);
};

const useHiddenTextLayout = (
  hiddenTextRef: MutableRefObject<HTMLElement | null>,
  containerRef: MutableRefObject<HTMLElement | null>
) => {
  const [dimensions, setDimensions] = useState<TextShimmerDimensions>({
    containerWidth: 0,
    lineHeight: 0,
    fontSize: 0,
    textAlign: 'left',
  });

  const measure = useCallback(() => {
    if (hiddenTextRef.current && containerRef.current) {
      const style = getComputedStyle(hiddenTextRef.current);

      setDimensions({
        fontSize: parseInt(style.fontSize.replace('px', ''), 10),
        lineHeight: hiddenTextRef.current.clientHeight,
        textAlign: style.textAlign as TextAlignment,
        containerWidth: containerRef.current.offsetWidth,
      });
    }
  }, [hiddenTextRef, containerRef]);
  useResizeCallback(measure);

  return dimensions;
};

export function TextShimmerLoader({ numLines = 1, width, ...otherProps }: TextShimmerLoaderProps) {
  const hiddenTextRef = useRef<HTMLElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const { fontSize, lineHeight, textAlign, ...boxProps } = otherProps;

  const dimensions = useHiddenTextLayout(hiddenTextRef, containerRef);
  const height = Math.ceil(numLines) * dimensions.lineHeight;
  const lines = Array(Math.floor(numLines))
    .fill(true)
    .map((_, index) => (
      <rect
        // eslint-disable-next-line react/no-array-index-key
        key={index}
        x="0"
        y={index * dimensions.lineHeight}
        rx="0"
        ry="0"
        width={dimensions.containerWidth}
        height={dimensions.fontSize || 12}
      />
    ));

  const lastLineWidth = (numLines % 1) * dimensions.containerWidth;
  if (lastLineWidth > 0) {
    const xPositions = {
      left: 0,
      right: dimensions.containerWidth - lastLineWidth,
      center: (dimensions.containerWidth - lastLineWidth) / 2,
    };
    lines.push(
      <rect
        key="last"
        x={xPositions[dimensions.textAlign]}
        y={Math.floor(numLines) * dimensions.lineHeight}
        rx="0"
        ry="0"
        width={lastLineWidth}
        height={dimensions.fontSize}
      />
    );
  }

  return (
    <>
      <HiddenText
        ref={hiddenTextRef}
        fontSize={fontSize}
        style={{
          lineHeight,
          textAlign,
        }}
        aria-hidden="true"
      >
        Sample text for shimmer sizing
      </HiddenText>

      <ReffableBox ref={containerRef} {...boxProps} width={width} height={`${height}px`}>
        <ContentLoader
          gradientRatio={2}
          width={dimensions.containerWidth}
          height={height}
          speed={0.99}
          interval={0.2}
          primaryColor={theme.colors.bmtGrey10}
          secondaryColor={theme.colors.bmtGrey20}
        >
          {lines}
        </ContentLoader>
      </ReffableBox>
    </>
  );
}

export interface DynamicShimmerLoaderProps {
  width?: BoxProps['width'];
  height: BoxProps['height'];
  primaryColor?: string;
  secondaryColor?: string;
  rx?: number;
  ry?: number;
}

export const DynamicShimmerLoader: React.FC<React.PropsWithChildren<DynamicShimmerLoaderProps>> = ({
  width,
  height,
  primaryColor,
  secondaryColor,
  rx,
  ry,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [dimensions, setDimensions] = useState({
    containerWidth: 0,
    containerHeight: 0,
  });

  const measure = useCallback(() => {
    if (containerRef.current) {
      setDimensions({
        containerWidth: containerRef.current.offsetWidth,
        containerHeight: containerRef.current.offsetHeight,
      });
    }
  }, [containerRef]);

  useResizeCallback(measure);

  return (
    <ReffableBox ref={containerRef} width={width} height={height}>
      <ContentLoader
        gradientRatio={2}
        width={dimensions.containerWidth}
        height={dimensions.containerHeight}
        speed={0.99}
        interval={0.2}
        primaryColor={primaryColor || theme.colors.bmtGrey10}
        secondaryColor={secondaryColor || theme.colors.bmtGrey20}
      >
        <rect x="0" y="0" width={dimensions.containerWidth} height={dimensions.containerHeight} rx={rx} ry={ry} />
      </ContentLoader>
    </ReffableBox>
  );
};
